import { ErrorBanner, PrimaryButton } from '@/components';
import { Sidepanel } from '@/components/layout';
import { useCurrentCompany } from '@/hooks/useCurrentCompany';
import { useCallback, useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useCreateFileSubmit } from '../hooks/useCreateFileSubmit';
import { useDocumentMetadataState } from '../hooks/useDocumentMetadataState';
import type { DocumentFile } from '../types';
import { DocumentDeviceAssignmentSection, DocumentDeviceAssignmentState } from './fileDetailPanel/DocumentDeviceAssignmentSection';
import { createFileObjectsSectionActions } from './fileDetailPanel/DocumentFileObjectsSection';
import { DocumentMetadataSection, DocumentMetadataState } from './fileDetailPanel/DocumentMetadataSection';
import { DocumentUploadStatusBanner } from './fileDetailPanel/DocumentUploadStatusBanner';
import { FileObjectRow } from './fileDetailPanel/types';
import { useFileObjectRows } from './fileDetailPanel/useFileObjectRows';
import { SingleFileObject } from './fileDetailPanel/SingleFileObject';
import { getFileBaseName } from '@/utils/getFileBaseName';
import { SidebarContent, SidebarFooter } from '@/components/layout/sidepanel/SidebarFooter';


/**
 * Sidebar panel for creating a new document file.
 * Manages all form state (metadata, device assignment, single file object), upload lifecycle,
 * and orchestrates submission via `useCreateFileSubmit`. Resets itself on each new session.
 */
export function CreateFileSidebar({
  isOpen,
  sessionId,
  initialFiles,
  initialFolder,
  currentSearch,
  onClose,
  onCreated,
  onRetryUpload,
}: CreateFileSidebarProps) {
  const { t } = useTranslation();
  const { companyId } = useCurrentCompany();

  // Only the first file is used — single-file-object assumption.
  const initialRows = useMemo(() => {
    const firstFile = initialFiles?.[0];
    if (!firstFile) return undefined;
    return [{ file: firstFile, values: {...firstFile, languageId: null, isAvailable: false } }];
  }, [initialFiles]);

  const [createdFile, setCreatedFile] = useState<DocumentFile | null>(null);

  const { state: fileObjectsState, actions: fileObjectsActions } =
    useFileObjectRows({ initialRows, fileId: createdFile?.id }, 'New File');

  const [appliedSessionId, setAppliedSessionId] = useState<string | null>(null);
  const {
    metadataState,
    metadataValueState,
    setMetadata,
    resetWith: resetMetadataWith,
  } = useDocumentMetadataState(buildInitialMetadataState(initialFiles, initialFolder, companyId));
  const [showErrors, setShowErrors] = useState(false);
  const [deviceAssignment, setDeviceAssignment] =
    useState<DocumentDeviceAssignmentState | undefined>(undefined);
  const [deviceAssignmentHasError, setDeviceAssignmentHasError] = useState(false);
  const [generalError, setGeneralError] = useState('');

  const hasCreatedSession = Boolean(createdFile);
  const shouldShowUserLevel = deviceAssignment !== undefined;
  const { rows, hasUploadingRows, hasFailedRows: hasUploadFailures } = fileObjectsState;
  const hasStartedUploads = rows.some(
    (row: FileObjectRow) => row.uploadStatus !== 'idle'
  );

  const validateForm = useCallback(() => {
    const rowsValid = fileObjectsActions.validate();
    setShowErrors(true);
    return !metadataValueState.hasErrors && !deviceAssignmentHasError && rowsValid;
  }, [fileObjectsActions, metadataValueState.hasErrors, deviceAssignmentHasError]);

  const handleGeneralError = useCallback((error: string) => {
    setGeneralError(error);
  }, []);

  const { handleSubmit, status } = useCreateFileSubmit({
    companyId,
    currentFolderId: initialFolder?.id,
    currentSearch,
    metadataState,
    deviceAssignment,
    shouldShowUserLevel,
    rows,
    hasCreatedSession,
    validateForm,
    fileObjectsActions,
    onCreated: (file) => {
      setCreatedFile(file);
      onCreated?.(file);
    },
    onGeneralError: handleGeneralError,
  });

  const isMetadataLocked = hasCreatedSession || status.isPending;

  const uploadBannerState = useMemo<UploadBannerState | null>(() => {
    if (!createdFile) return null;
    if (!hasStartedUploads || hasUploadingRows || status.isPending) return 'loading';
    return hasUploadFailures ? 'error' : 'success';
  }, [status.isPending, createdFile, hasStartedUploads, hasUploadingRows, hasUploadFailures]);

  const isFormValid = useMemo(() => {
    if (metadataValueState.hasErrors) return false;
    if (deviceAssignmentHasError) return false;
    return fileObjectsState.isValid;
  }, [metadataValueState.hasErrors, deviceAssignmentHasError, fileObjectsState.isValid]);

  // Sync companyId into metadata state when it changes.
  useEffect(() => {
    if (metadataState.companyId === companyId) return;
    setMetadata({ ...metadataState, companyId }, metadataValueState.hasErrors);
  }, [companyId, metadataState, metadataValueState.hasErrors, setMetadata]);

  // Reset all form state when a new session is opened.
  useEffect(() => {
    if (!isOpen || !sessionId || sessionId === appliedSessionId) return;

    const initialMetadata = buildInitialMetadataState(initialFiles, initialFolder, companyId);
    setAppliedSessionId(sessionId);
    resetMetadataWith(initialMetadata);
    setMetadata(initialMetadata, false);
    setShowErrors(false);
    setDeviceAssignment(undefined);
    setDeviceAssignmentHasError(false);
    setGeneralError('');
    setCreatedFile(null);
    fileObjectsActions.reset(initialRows);
  }, [
    appliedSessionId,
    initialFolder,
    initialFiles,
    initialRows,
    isOpen,
    sessionId,
    fileObjectsActions,
    companyId,
    resetMetadataWith,
    setMetadata,
  ]);

  const handleRetryUpload = useCallback(() => {
    if (!createdFile || !onRetryUpload) return;
    onRetryUpload(createdFile);
  }, [createdFile, onRetryUpload]);

  const handleDeviceAssignmentChange = useCallback(
    (state: DocumentDeviceAssignmentState | undefined, hasError: boolean) => {
      setDeviceAssignment(state);
      setDeviceAssignmentHasError(hasError);
    },
    []
  );

  const sectionActions = useMemo(
    () => createFileObjectsSectionActions(fileObjectsActions),
    [fileObjectsActions]
  );

  return (
    <Sidepanel
      isOpen={isOpen}
      onClose={onClose}
      title={createdFile?.name || t('documents.createFile.title')}
    >
      <SidebarContent className='text-onbackground'>

        <FormSections
          metadataState={metadataState}
          deviceAssignment={deviceAssignment}
          fileObjectsState={fileObjectsState}
          sectionActions={sectionActions}
          isMetadataLocked={isMetadataLocked}
          showErrors={showErrors}
          onMetadataChange={setMetadata}
          initialFolder={initialFolder}
          onDeviceAssignmentChange={handleDeviceAssignmentChange}
        />


        <ErrorBanner hasError={generalError.length > 0}>
          {generalError}
        </ErrorBanner>

        {uploadBannerState && (
          <DocumentUploadStatusBanner
            state={uploadBannerState}
            onRetry={uploadBannerState === 'error' ? handleRetryUpload : undefined}
          />
        )}
      </SidebarContent>
      <SidebarFooterInt
        createdFile={createdFile}
        isFormValid={isFormValid}
        isPending={status.isPending}
        onClose={onClose}
        onSubmit={() => handleSubmit()}
      />
    </Sidepanel>
  );
}

type UploadBannerState = 'loading' | 'success' | 'error';

interface CreateFileSidebarProps {
  isOpen: boolean;
  sessionId: string | null;
  initialFiles?: File[];
  initialFolder: { name: string; id: string } | null;
  currentSearch?: string;
  onClose: () => void;
  onCreated?: (file: DocumentFile) => void;
  onRetryUpload?: (file: DocumentFile) => void;
}

interface FormSectionsProps {
  metadataState: DocumentMetadataState;
  deviceAssignment: DocumentDeviceAssignmentState | undefined;
  fileObjectsState: ReturnType<typeof useFileObjectRows>['state'];
  sectionActions: ReturnType<typeof createFileObjectsSectionActions>;
  isMetadataLocked: boolean;
  initialFolder: { name: string; id: string } | null;
  showErrors: boolean;
  onMetadataChange: (state: DocumentMetadataState, hasError: boolean) => void;
  onDeviceAssignmentChange: (state: DocumentDeviceAssignmentState | undefined, hasError: boolean) => void;
}

interface SidebarFooterProps {
  createdFile: DocumentFile | null;
  isFormValid: boolean;
  isPending: boolean;
  onClose: () => void;
  onSubmit: () => void;
}



// Builds the default DocumentMetadataState for a new sidebar session.
function buildInitialMetadataState(
  initialFiles: File[] | undefined,
  initialFolder: { name: string; id: string } | null,
  companyId: string | null
): DocumentMetadataState {
  return {
    name: initialFiles?.[0] ? getFileBaseName(initialFiles[0].name) : '',
    selectedFolder: initialFolder?.id ?? null,
    category: '0',
    selectedCompanyLevel: undefined,
    companyId,
  };
}

// Renders the three form sections stacked vertically:
// 1. DocumentMetadataSection + language select
// 2. DocumentDeviceAssignmentSection
// 3. SingleFileObject — file upload/view for the single file object
function FormSections({
  metadataState,
  deviceAssignment,
  fileObjectsState,
  sectionActions,
  isMetadataLocked,
  showErrors,
  initialFolder,
  onMetadataChange,
  onDeviceAssignmentChange,
}: FormSectionsProps) {
  const firstRow = fileObjectsState.rows[0];

  useEffect(() => {
    if (firstRow?.file && !metadataState.name) {
      onMetadataChange({ ...metadataState, name: getFileBaseName(firstRow.file.name) }, false);
    }
  }, [firstRow?.file]);


  return (
    <div className="space-y-6">
      <DocumentMetadataSection
        initialFolder={initialFolder}
        state={metadataState}
        isLocked={isMetadataLocked}
        showErrors={showErrors}
        hideFolderSelection
        onStateChange={onMetadataChange}
        langauageSetting={{
          firstRow: firstRow,
          isLocked: isMetadataLocked,
          sectionActions: sectionActions,
        }}
      />
      {firstRow ? (
        <SingleFileObject
          row={firstRow}
          isLocked={isMetadataLocked}
          mode='create'
          onFileSelect={sectionActions.onFileSelect}
          onValidationError={sectionActions.onValidationError}
          onOpen={sectionActions.onOpen}
          onUpload={sectionActions.onUploadFile}
        />
      ) : null}

      <DocumentDeviceAssignmentSection
        state={deviceAssignment}
        isLocked={isMetadataLocked}
        showErrors={showErrors}
        onStateChange={onDeviceAssignmentChange}
      />
    </div>
  );
}

// Renders the action bar at the bottom of the sidebar.
function SidebarFooterInt({
  createdFile,
  isFormValid,
  isPending,
  onClose,
  onSubmit,
}: SidebarFooterProps) {
  const { t } = useTranslation();

  return (
    <SidebarFooter className="border-border flex items-center justify-end gap-3 border-t pt-4">
      <button
        type="button"
        onClick={onClose}
        className="text-onsurface/70 hover:text-onsurface text-sm font-medium transition-colors"
      >
        {createdFile
          ? t('documents.createFile.closeButton')
          : t('documents.createFile.cancelButton')}
      </button>
      {!createdFile && (
        <PrimaryButton
          onClick={onSubmit}
          disabled={!isFormValid}
          isLoading={isPending}
        >
          {t('documents.createFile.createButton')}
        </PrimaryButton>
      )}
    </SidebarFooter>
  );
}
